<template>
  <ErrorBoundary>
    <div class="flex justify-between items-center text-sm">
      <div>
        <label class="font-medium">
          {{
            isBoxEvent
              ? $t('product.show.boxDetails.buyBoxQuantity')
              : $t('product.show.checkout.quantity')
          }}
        </label>
        <span
          class="block text-xs font-normal text-gray-700 mr-2"
          :class="{ 'text-red': triedToGoBeyondMax || triedToGoBelowMin }"
        >
          {{ subtitle }}
        </span>
      </div>

      <NumberControlButtons
        v-model="quantity"
        class="max-w-[5rem]"
        :min="minQty"
        :disable-minus="quantity <= 1"
        :is-dates-loaded="isDatesLoaded"
        @min-reached="handleMinReached"
      />
    </div>
  </ErrorBoundary>
</template>

<script lang="ts" setup>
import type { EventDate } from '~/composables/types/event'
const { t } = useI18n()

const props = defineProps<{
  selectedDate: EventDate
  isBookingAsPrivate: boolean
  isBoxEvent: boolean
  isDatesLoaded: boolean
}>()

const isInitialized = ref(props.isBookingAsPrivate)

const emit = defineEmits(['triedToGoBeyondMax'])

const privateMinTickets = computed(() =>
  eventDateGetters.getPrivateMinTickets(props.selectedDate)
)
const privateMaxTickets = computed(() =>
  eventDateGetters.getPrivateMaxTickets(props.selectedDate)
)

const publicMaxTickets = computed(() =>
  eventDateGetters.getAvailableTicketsQuantity(props.selectedDate)
)

const availableTickets = computed(() =>
  props.isBookingAsPrivate ? privateMaxTickets.value : publicMaxTickets.value
)

const minQty = computed(() =>
  props.isBookingAsPrivate ? privateMinTickets.value : 1
)

const maxQty = computed(() => availableTickets.value || 1)

const quantity = defineModel<number>({ default: 1 })

const errorState = ref<'below-min' | 'beyond-max' | null>(null)

const triedToGoBelowMin = computed(() => errorState.value === 'below-min')
const triedToGoBeyondMax = computed(() => errorState.value === 'beyond-max')

const subtitle = computed(() => {
  if (triedToGoBelowMin.value) {
    return t('product.show.checkout.triedToAddBelowMinTickets', {
      minQty: minQty.value,
    })
  }

  if (triedToGoBeyondMax.value) {
    return t('product.show.checkout.triedToAddBeyondMaxTickets')
  }

  if (props.isBookingAsPrivate) {
    return t('product.show.checkout.buyBoxPrivateQuantityHelperText')
  }
  return props.isBoxEvent
    ? t('product.show.boxDetails.buyBoxQuantityHelperText')
    : t('product.show.checkout.privateQuantityNoTicketsSelected')
})

const handleMinReached = () => {
  if (!isInitialized.value) {
    isInitialized.value = true
    return
  }
  errorState.value = 'below-min'
}

watchEffect(() => {
  if (quantity.value > maxQty.value) {
    errorState.value = 'beyond-max'
    emit('triedToGoBeyondMax', true)
  } else if (quantity.value < minQty.value) {
    errorState.value = 'below-min'
  } else {
    errorState.value = null
  }
})
</script>
